/** Idioma do painel — dois, tipados, sem dependência nova.
 *
 * `react-i18next` resolveria o mesmo com plural e interpolação prontos, e
 * custa ~40 kB no bundle de um painel que abre no 4G do celular do
 * mecânico. Para dois idiomas, o dicionário tipado ganha: são ~60 linhas
 * de infraestrutura e o TypeScript recusa chave que não existe — o que o
 * i18next só descobre em runtime, com a chave crua aparecendo na tela.
 *
 * ## Quem decide o idioma
 *
 * `User.locale` → `Garage.locale` → o navegador. A mesma ordem do backend
 * (apps/common/locale.py), e pela mesma razão: a recepcionista que entrou
 * hoje não deveria configurar idioma nenhum, e o dono brasileiro da
 * oficina em Miami quer o painel dele em português.
 *
 * ## Como escrever uma chave nova
 *
 * Acrescente em `en` — é ele que define o tipo. O `pt` é `Record<Chave,
 * string>`, então esquecer a tradução **não compila**. É de propósito:
 * painel metade traduzido parece defeito, não parece falta de tradução.
 */

import { createContext, createElement, useContext, type ReactNode } from "react";

import * as fmt from "./format";

export const en = {
  "gallery.title": "Photo gallery",
  "gallery.hint": "Show your shop, your team and your work. Add up to 6 photos and arrange them in your preferred order.",
  "gallery.limit": "Your gallery can have up to 6 photos.",
  "gallery.formats": "Use JPG, PNG or WebP images.",
  "gallery.size": "Each photo can be up to 5 MB.",
  "gallery.optional": "photos · optional",
  "gallery.add": "Add photos",
  "gallery.empty": "Start with your storefront, workspace or a completed job. The gallery only appears on your site when you add a photo.",
  "gallery.photo": "Photo",
  "gallery.up": "Move photo up",
  "gallery.down": "Move photo down",
  "gallery.cover": "Use as cover",
  "gallery.caption": "Caption (optional)",
  "gallery.saveCaption": "Save caption",
  "gallery.saving": "Publishing photos…",
  "gallery.autosave": "Gallery changes are published immediately. JPG, PNG or WebP, up to 5 MB per photo.",
  "gallery.retry": "Try again",

  // ---------------------------------------------------------- navegação
  "nav.agenda": "Schedule",
  "nav.myAgenda": "My schedule",
  "nav.customers": "Customers",
  "nav.vehicles": "Vehicles",
  "nav.services": "Services",
  "nav.team": "Team",
  "nav.reports": "Reports",
  "nav.settings": "Settings",
  "nav.site": "Public page",
  "nav.subscription": "Subscription",
  "nav.profile": "My profile",
  "nav.signOut": "Sign out",
  "nav.openMenu": "Open menu",
  "nav.closeMenu": "Close menu",

  // ------------------------------------------------------------- comum
  "common.save": "Save",
  "common.saving": "Saving…",
  "common.saved": "Saved",
  "common.cancel": "Cancel",
  "common.back": "Back",
  "common.continue": "Continue",
  "common.confirm": "Confirm",
  "common.delete": "Delete",
  "common.edit": "Edit",
  "common.add": "Add",
  "common.search": "Search",
  "common.loading": "Loading…",
  "common.none": "None",
  "common.optional": "optional",
  "common.required": "required",
  "common.yes": "Yes",
  "common.no": "No",
  "common.close": "Close",
  "common.today": "Today",
  "common.name": "Name",
  "common.email": "Email",
  "common.phone": "Phone",
  "common.notes": "Notes",
  "common.active": "Active",
  "common.inactive": "Inactive",
  "common.error": "Something went wrong. Try again.",
  "common.empty": "Nothing here yet.",

  // ------------------------------------------------- estado da reserva
  "status.pending": "Pending",
  "status.confirmed": "Confirmed",
  "status.in_progress": "In service",
  "status.completed": "Done",
  "status.canceled": "Canceled",
  "status.no_show": "No-show",

  // ----------------------------------------------------------- agenda
  "agenda.title": "Schedule",
  "agenda.new": "New appointment",
  "agenda.prevDay": "Previous day",
  "agenda.nextDay": "Next day",
  "agenda.allTechs": "All technicians",
  "agenda.appointments": "Appointments",
  "agenda.expected": "Expected today",
  "agenda.canceledNoShows": "Canceled / no-shows",
  "agenda.emptyTitle": "No appointments on this day",
  "agenda.emptyText": "Use the button above to book a customer.",
  "agenda.noShowHistory": "{n} no-show(s) on record",
  "agenda.mine": "My schedule",
  "agenda.team": "Team",
  "agenda.todayShort": "Today",
  "agenda.dayOff": "Block time",
  "agenda.nothingToday": "Nothing booked for this day",
  "agenda.nothingTodayText": "Enjoy the quiet — or block the time so nobody books it.",
  "agenda.viewDay": "Day",
  "agenda.viewWeek": "Week",
  "agenda.viewMonth": "Month",
  "agenda.prevWeek": "Previous week",
  "agenda.nextWeek": "Next week",
  "agenda.prevMonth": "Previous month",
  "agenda.nextMonth": "Next month",
  "agenda.expectedPeriod": "Expected in period",
  "agenda.openDay": "Open {day}",
  "agenda.morePlus": "+{n} more",

  "mine.noProfileTitle": "My schedule",
  "mine.noProfileText":
    "Your login isn't linked to a technician profile yet. Ask the shop owner to link it under <b>Team</b>.",
  "mine.production": "Today's production",
  "mine.next": "Next up",
  "mine.bookCustomer": "Book a customer",
  "mine.block": "Block",
  "mine.freeDay": "Free day",
  "mine.freeDayText": "Nothing booked. Enjoy it, or book a customer.",
  "mine.missedTimes": "Missed {n}×",

  // ------------------------------------------------------------ ações
  "action.confirm": "Confirm",
  "action.cancel": "Cancel",
  "action.start": "Start",
  "action.noShow": "No-show",
  "action.complete": "Finish",

  // -------------------------------------------------------------- home
  "home.todayAppointments": "Appointments today",
  "home.todayBilled": "Billed today",
  "home.onlyCompleted": "Completed jobs only",
  "home.monthBilled": "Billed this month",
  "home.monthNoShows": "No-shows this month",
  "home.noShowHint": "WhatsApp reminders cut this down",
  "home.todayStatus": "Today at a glance",
  "home.publicPage": "Public page",
  "home.setupPending": "Finish setting up",
  "home.setupHours": "opening hours",
  "home.setupServices": "services",
  "home.setupTeam": "who does the work",

  "home.hello": "Hi, {nome}",
  "home.onTrialOf": "You're on the trial for the <b>{plano}</b> plan.",
  "home.monthAppointments": "{n} appointments",
  "home.publicPageText": "The link your customers use to book on their own.",
  "home.seeFullAgenda": "See the full schedule",
  "home.notAcceptingTitle": "Your page isn't taking bookings yet",
  "home.notAcceptingText": "Still missing: {itens}.",
  "home.finishSetup": "Finish setup",

  // ----------------------------------------------------------- entrar
  "auth.panelSubtitle": "Shop dashboard",
  "auth.password": "Password",
  "auth.signIn": "Sign in",
  "auth.wrongCredentials": "Wrong email or password.",
  "auth.newPasswordTitle": "Create your password",
  "auth.newPassword": "New password",
  "auth.minChars": "At least 8 characters.",
  "auth.minCharsPrivate": "At least 8 characters. Not even the shop owner sees this password.",
  "auth.inviteTitle": "Invitation",
  "auth.inviteText": "You've been invited to the dashboard of this shop.",
  "auth.inviteUnavailable": "Invitation unavailable",
  "auth.inviteUsed": "This invitation was already used. If the account is yours, just sign in.",
  "auth.inviteExpired": "This link expired or no longer exists. Ask the shop owner for a new one.",
  "auth.fullName": "Your full name",
  "auth.createPassword": "Create a password",
  "auth.acceptInvite": "Accept invitation",
  "auth.creatingAccess": "Creating my access…",

  // -------------------------------------------------------- cadastro
  "signup.subtitle": "Create your account in a minute",
  "signup.chosenPlan": "Chosen plan",
  // ⚠ O "3" tem dono: apps/api/signup.TRIAL_MONTHS. Esta tela é anterior
  // ao login e não tem API para consultar, então o número é repetido —
  // e test_early_access.py falha se os dois divergirem.
  "signup.trialNote": "3 months free. No credit card now.",
  "signup.upTo": "up to {n} technicians",
  "signup.shopName": "Shop name",
  "signup.shopNamePlaceholder": "Joe's Auto",
  "signup.pageAddress": "Your page address",
  "signup.yourName": "Your name",
  "signup.passwordHint": "At least 10 characters.",
  "signup.submit": "Create my shop",
  "signup.failed": "We couldn't create the account. Try again.",

  // ----------------------------------------------------- configuração
  "setup.eyebrow": "Setup",
  "setup.title": "Let's get your shop ready",
  "setup.doneTitle": "Your page is taking bookings.",
  "setup.intro": "Three steps and your customers can book on their own.",
  "setup.team": "Who does the work",
  "setup.hours": "Opening hours",
  "setup.services": "Your services",
  "setup.whenOpen": "When is the shop open?",
  "setup.whatOffer": "What do you offer?",
  "setup.displayName": "Name the customer sees",
  "setup.finish": "Finish setup",
  "setup.live": "Your page is live",

  "setup.allSet": "All set!",
  "setup.whoWorks": "Who does the work?",
  "setup.hoursHint": "We pre-filled the most common one. Adjust what's different.",
  "setup.servicesHint":
    "Check what the shop does and adjust the price. The values are market averages — almost nobody charges the average. Duration and buffers live in the dashboard.",
  "setup.teamHint":
    "Start with yourself. You add the rest of the crew later under <b>Team</b>, with commission and a login for each.",
  "setup.willRun": "Will perform the {n} services you added. You can change that any time.",
  "setup.priceOf": "Price of {servico}",
  "setup.liveHint":
    "Paste this link in your Instagram bio and on Google. This is where your customers book on their own, around the clock.",

  // ------------------------------------------------------------ serviços
  "services.emptyTitle": "No services yet",
  "services.emptyText":
    "Add oil changes, washes, inspections and whatever else the shop offers.",
  "services.addOne": "Add a service",
  "services.edit": "Edit service",
  "services.new": "New service",
  "services.duration": "Duration (min)",
  "services.price": "Price ({moeda})",
  "services.bufferBefore": "Before (min)",
  "services.bufferAfter": "After (min)",
  "services.requiresVehicle": "Requires the license plate",

  // ------------------------------------------------------------ clientes
  "customers.search": "Search by name or phone",
  "customers.noneFound": "No customers found",
  "customers.noneYet": "No customers yet",
  "customers.tryAnother": "Try another name or phone number.",
  "customers.emptyText":
    "Add the first one, import your whole list at once, or let them show up as you book.",

  // ------------------------------------------------------------ veículos
  "vehicles.search": "Plate, make, model or owner",
  "vehicles.noneFound": "No vehicles found",
  "vehicles.noneYet": "No vehicles yet",
  "vehicles.tryPlate": "Try the plate without the dash, or the owner's name.",
  "vehicles.emptyText":
    "Every booking with a plate creates the car by itself. Here you fill in the rest.",
  "vehicles.edit": "Edit vehicle",
  "vehicles.new": "New vehicle",
  "vehicles.owner": "Owner",
  "vehicles.pickCustomer": "Pick the customer",
  "vehicles.make": "Make",
  "vehicles.model": "Model",
  "vehicles.year": "Year",
  "vehicles.color": "Color",
  "vehicles.odometer": "Odometer",
  "vehicles.odometerHint": "The number that says whether the service is overdue.",
  "vehicles.type": "Type",
  "vehicles.fuel": "Fuel",
  "vehicles.notesPlaceholder": "e.g. engine was rebuilt in 2023",
  "vehicles.noHistory": "Nothing on record yet",
  "vehicles.noHistoryText":
    "The history starts with the first completed job on this vehicle.",

  "services.subtitle": "Duration and price your customers see.",
  "services.buffers": "Buffers",
  "services.buffersHint":
    "Time between jobs to get the car off the lift, wash your hands and take payment. Without it the schedule is optimistic and the tech runs late.",
  "services.onlineBookable": "Available for booking on the public page",
  "services.requiresVehicleHint":
    "Leave it on for anything that comes into the shop. Turn it off only for what has no car — a quote over the phone, a part sold at the counter.",
  "services.kind": "Type",
  "services.kindService": "Service",
  "services.kindQuote": "Quote",
  "services.kindServiceHint": "Work you do on the car, at the price you set.",
  "services.kindQuoteHint":
    "The visit where the customer comes to find out the price. No charge — set how long your shop takes to look the car over, and write the amount on the job itself.",
  "services.quote": "Quote",
  "services.noCharge": "No charge",

  // ------------------------------------------------- ficha do atendimento
  "detail.title": "Job details",
  "detail.open": "Open {name}'s job",
  "detail.quote": "Quote",
  "detail.notQuotedYet": "Not quoted",
  "detail.customerNotes": "What the customer asked for",
  "detail.quotedPrice": "Quoted amount",
  "detail.quotedPriceHint":
    "What you told the customer the repair will cost. It stays out of the day's expected revenue until the job is actually booked.",
  "detail.report": "Vehicle report",
  "detail.reportHint": "What you found on the car. Read again when it comes back.",
  "detail.reportHintQuote":
    "Describe the car: what's damaged, what needs replacing, what can wait. This is what the customer came for.",
  "detail.internalNotes": "Internal note",
  "detail.internalNotesHint": "For the shop only — the customer never sees it.",
  "detail.saved": "Saved",
  "detail.history": "History",
  "detail.noHistory": "Nothing recorded yet.",

  "photos.title": "Vehicle check",
  "photos.hint":
    "How the car arrived, and how it left. It's what settles \u201cthat scratch was already there\u201d. Only your team can see these.",
  "photos.arrival": "Arrival",
  "photos.departure": "Departure",
  "photos.add": "Take photo",
  "photos.full": "Limit reached",
  "photos.remove": "Delete photo",
  "photos.format": "Use a JPG, PNG or WebP image.",
  "photos.failed": "Couldn't load this photo.",

  "event.created": "created",
  "event.confirmed": "confirmed",
  "event.started": "started",
  "event.completed": "completed",
  "event.canceled": "canceled",
  "event.noShow": "marked no-show",
  "event.rescheduled": "rescheduled",
  "event.quoted": "changed the quoted amount",
  "event.photoAdded": "added a photo",
  "event.photoRemoved": "deleted a photo",

  "vehicles.fleetCount": "{n} vehicle(s) in the shop's fleet",
  "vtype.car": "Car",
  "vtype.suv": "SUV / Pickup",
  "vtype.van": "Van",
  "vtype.motorcycle": "Motorcycle",
  "vtype.truck": "Truck",
  "vtype.other": "Other",
  "fuel.none": "Not set",
  "fuel.flex": "Flex",
  "fuel.gasoline": "Gasoline",
  "fuel.ethanol": "Ethanol",
  "fuel.diesel": "Diesel",
  "fuel.cng": "CNG",
  "fuel.hybrid": "Hybrid",
  "fuel.electric": "Electric",

  // ------------------------------------------------------ configurações
  "settings.loadFailed": "We couldn't load the schedule rules.",
  "settings.subtitle":
    "How your shop takes and organizes bookings. Applies to the site, WhatsApp and the dashboard.",
  "settings.slotsHint": "Which times the customer sees, and how late they can change things.",
  "settings.minAdvance": "Minimum notice (minutes)",
  "settings.minAdvanceHint": "Stops a booking for \u201Cfive minutes from now\u201D. Zero opens the next slot.",
  "settings.horizonHint": "How far out a customer can book. A long horizon raises no-shows.",
  "settings.cancelHint":
    "Below this the customer can't cancel alone: the page shows the shop's WhatsApp instead.",
  "settings.rescheduleHint":
    "Same idea for changing the time. You and the front desk have no such limit.",
  "settings.sameDay": "Accept same-day bookings",
  "settings.sameDayHint": "Off, the customer can only book from tomorrow on.",
  "settings.autoConfirmHint":
    "Off, every booking starts pending and waits for your \u201CConfirm\u201D on the schedule — useful to vet a new customer before holding a bay.",
  "settings.overbookHint":
    "Allows booking off the grid: outside opening hours or ignoring the notice period. Two customers in the same slot stay impossible.",
  "settings.teamBehavior": "Team behavior",
  "settings.teamBehaviorHint": "The two exceptions every shop asks for differently.",
  "settings.remindersHint": "What the customer gets without you doing anything.",
  "settings.reminder24Hint":
    "A booking made less than 24h ahead doesn't get this one — only the 2h reminder.",
  "settings.reminder2Hint":
    "This is what cuts no-shows the most. Turn it off only if the shop already calls.",
  "settings.saveRules": "Save rules",

  // -------------------------------------------------------------- equipe
  "team.withLogin": "Has a login",
  "team.inviteSent": "Invite sent",
  "team.noLogin": "No login",
  "reports.sevenDays": "7 days",
  "team.emptyTitle": "No technicians yet",
  "team.edit": "Edit technician",
  "team.new": "New technician",
  "team.commission": "Commission (%)",
  "team.commissionLocked": "Only the shop owner changes the commission.",
  "team.agendaColor": "Color on the schedule",
  "team.taxIdInvalid": "Invalid {documento}. Check the numbers.",
  "team.taxIdHint": "Needed for payouts. They can fill it in later under My profile.",
  "team.photoHint": "Shows up in your site's team section. A centered face works best.",
  "team.bioPlaceholder": "e.g. Specialist in fuel injection and automatic transmissions.",
  "team.shownOnce": "it won't be shown again",

  // --------------------------------------------------------- relatórios
  "reports.thisMonth": "This month",
  "reports.lastMonth": "Last month",
  "reports.loadFailed": "We couldn't load the report",
  "reports.checkPeriod": "Check the period you picked and try again.",
  "reports.avgTicket": "Average ticket",
  "reports.perAppointment": "Per appointment, not per service",
  "reports.noneCompleted": "No completed jobs in this period",
  "reports.noneCompletedHint":
    "Revenue counts from the moment you finish the job on the schedule.",
  "reports.byTech": "By technician",
  "reports.byTechHint": "Who produced in the period",
  "reports.byService": "By service",
  "reports.byServiceHint": "What the shop sells most",

  // ----------------------------------------------------------- meu site
  "site.loadFailed": "We couldn't load your site identity.",
  "site.title": "The face of your shop",
  "site.subtitle": "What you change here shows up on your public page.",
  "site.identity": "Name, logo and the big photo at the top.",
  "site.logoHint": "Shows at the top of the site. A PNG with a transparent background works best.",
  "site.coverHint":
    "The background at the top. Wide and horizontal — it's the one that goes in the WhatsApp card.",
  "site.buttonsLinks": "Buttons and links.",
  "site.displayFont": "Headline font",
  "site.beforeBooking": "What the customer reads before booking.",
  "site.headlineHint": "Empty uses the shop name.",
  "site.subheadlineHint": "Empty uses the default line.",
  "site.subheadlinePlaceholder":
    "Pick the service, the technician and the time. It takes under a minute and there is no account to create.",
  "site.about": "About the shop",
  "site.aboutPlaceholder":
    "e.g. In this neighborhood since 1998. Honest diagnosis, genuine parts, and a quote before we touch anything.",
  "site.whatToShow": "What to show",
  "site.showPrices": "Show prices",
  "site.showPricesHint": "Off, the customer still picks the service — they just don't see the price.",
  "site.showTeam": "Show team photos",
  "site.showTeamHint": "Photos are set under Team, in each technician's profile.",
  "site.socialHint": "They become links in the site footer.",
  "site.savePublish": "Save and publish",

  // -------------------------------------------------------- assinatura
  "sub.ownerOnly": "Only the shop owner can see and change the subscription.",
  "sub.loadFailed": "We couldn't load your subscription. Try again in a moment.",
  "sub.canceledNote": "Canceled. Access continues until the end of the period you paid for.",
  "sub.taxIdInvalid": "Check the numbers: it needs to be a valid {documento}.",
  "sub.billingData": "Billing details",
  "sub.creditCard": "Credit card",
  "sub.trialLeft": "Free trial — {n} day(s) left, no card on file.",
  "sub.active": "Active.",
  "profile.pixPhone": "Mobile",
  "sub.choosePlan": "Choose your plan",
  "sub.updateBilling": "Update billing",
  "sub.status": "Status",
  "sub.whyLeaving": "What made you leave? (optional)",

  // -------------------------------------------------------- meu perfil
  "profile.loadFailed": "We couldn't load your profile. Try again in a moment.",
  "profile.photoHint":
    "Shows in the team section of the shop's site. A centered face works best.",
  "profile.displayName": "Display name",
  "profile.notSet": "Not set",
  "profile.currentPassword": "Current password",
  "profile.changePassword": "Change password",
  "profile.passwordChanged": "Password changed.",
  "profile.randomKey": "Random key",

  // ------------------------------------------------------------- modais
  "modal.editCustomer": "Edit customer",
  "modal.newCustomer": "New customer",
  "modal.fullNamePlaceholder": "First and last name",
  "modal.phoneHint": "This is where the booking reminder lands.",
  "modal.notesOptional": "Notes (optional)",
  "modal.notesPlaceholder": "e.g. prefers to be called in the afternoon",
  "modal.imageSelected": "Selected — save to publish.",
  "modal.alreadyThere": "already on file",
  "modal.withProblems": "with problems",
  "modal.nothingNew": "Nothing new to import",
  "modal.services": "Services",
  "modal.findCustomer": "Find a customer",
  "modal.nameOrPhone": "Name or phone",
  "modal.whichVehicle": "Which vehicle?",
  "modal.otherPlate": "Or another plate",
  "modal.newPlateHint": "A new plate creates the car along with the booking.",
  "modal.trialEndsToday": "Your trial ends today.",
  "modal.blockTime": "Block time",
  "modal.reasonOptional": "Reason (optional)",
  "modal.noReason": "No reason",
  "modal.lunch": "Lunch",
  "modal.personal": "Personal errand",
  "modal.doctor": "Doctor's appointment",
  "modal.dayOff": "Day off",
  "modal.repeatNewPassword": "Repeat the new password",
  "modal.passwordsDiffer": "The passwords don't match.",
  "modal.saveAndEnter": "Save and sign in",

  // ---------------------------------------------------------- papéis
  "role.owner": "Owner",
  "role.manager": "Manager",
  "role.receptionist": "Front desk",
  "role.professional": "Technician",

  // ------------------------------------------------------ menu do painel
  "nav.home": "Home",
  "nav.houseAgenda": "Shop schedule",
  "nav.mySite": "My page",
  "nav.scheduleRules": "Schedule rules",
  "nav.noPlan": "No plan",
  "nav.onTrial": " · on trial",

  // -------------------------------------------------- falhas de carga
  "boot.suspendedTitle": "Account suspended",
  "boot.suspendedText":
    "The subscription was not settled and access to the dashboard is blocked. Your data is still safe: schedule, customers and history come back as they were as soon as the payment clears.",
  "boot.noGarageTitle": "Account with no shop",
  "boot.noGarageText":
    "Your login works, but it isn't linked to an active shop yet. Ask the owner to send you an invite, or create your own shop.",
  "boot.failedTitle": "We couldn't load your account",
  "boot.failedText":
    "The server didn't answer as expected. Check your connection and try again.",
  "boot.reactivate": "Reactivate account",
  "boot.retry": "Try again",

  // ---------------------------------------------------------- duração
  "duration.minutesShort": "{n}min",
  "duration.hoursShort": "{h}h",
  "duration.hoursMinutes": "{h}h{m}",
  // ------------------------------------------ configuração (continuação)
  "hours.title": "Opening hours",
  "hours.hint": "The hours the shop takes cars in. Your page only offers times inside them.",
  "hours.loadFailed": "We couldn't load the opening hours.",
  "hours.timeOffHint": "Closing on one date — a holiday, a trip — is a block on the schedule, not a change here.",
  "hours.needOneDay": "Leave at least one day open. To close for a stretch, block those dates on the schedule.",
  "hours.save": "Save hours",
  "hours.saved": "Saved. Your page is already offering the new hours.",
  "hours.startOf": "{dia} — opens at",
  "hours.endOf": "{dia} — closes at",
  "hours.closed": "Closed",
  "hours.to": "to",
  "hours.removeRange": "remove",
  "hours.addBreak": "+ break (lunch)",
  "setup.saveContinue": "Save and continue",
  "setup.addServices": "Add {n} service(s)",
  "setup.laterPrefix": "You can do this later —",
  "setup.laterLink": "go to the dashboard",
  "setup.copy": "Copy",
  "setup.copied": "Copied",
  "setup.viewPage": "View my page",
  "setup.goDashboard": "Go to the dashboard",
  "setup.namePlaceholder": "Joe",

  // ------------------------------------------------------------ importação
  "import.title": "Import customers",
  "import.pickFile": "Choose file",
  "import.templateName": "customers-template.csv",
  "import.columns": "name, phone, email, birthday, notes",
  "import.needs": "name and phone with area code",

  // ----------------------------------------------------------- agendamento
  "modal.newAppointment": "New appointment",
  "modal.technician": "Technician",
  "modal.orCreateNow": "Or add them now",
  "modal.plate": "Plate",
  "modal.restOfDay": "Rest of the day",
  "modal.wholeDay": "Whole day",

  // ---------------------------------------------------------------- perfil
  "profile.title": "My profile",
  "profile.noProfileText":
    "Your account isn't linked to a technician profile, so there's no schedule or commission of your own. You can still change your password here.",
  "profile.photo": "Photo",
  "profile.saved": "Profile saved.",

  // -------------------------------------------------------------- clientes
  "customers.title": "Customers",

  // ------------------------------------------------------------ relatórios
  "reports.to": "To",
  "reports.byTechnician": "By technician",

  // ---------------------------------------------------------- configurações
  "settings.bookingHorizon": "Booking window (days)",
  "settings.autoConfirm": "Confirm bookings automatically",
  "settings.seeAllAgendas": "Technicians see every schedule",
  "settings.seeAllAgendasHint":
    "Off, each technician sees only their own customers. On, everyone sees the whole shop.",
  "settings.selfBooking": "Technicians can squeeze people in",
  "settings.selfBookingHint":
    "Lets a technician book on their own schedule outside the grid — the regular customer who shows up without an appointment.",

  // ------------------------------------------------------------- meu site
  "site.pageTitle": "My page",
  "site.lookHint": "Pick the look and the colors of the shop.",
  "site.visibilityHint": "Not every shop wants everything on display.",

  // ----------------------------------------------------------- assinatura
  "sub.title": "Subscription",
  "sub.backToDashboard": "Back to the dashboard",
  "sub.perYear": "year",
  "sub.suspended": "Account suspended.",
  "sub.subscribe": "Subscribe",
  "sub.billing": "Billing",

  // ---------------------------------------------------------------- equipe
  "team.emptyText": "Add your technicians — they can get a login later.",
  "team.addFirst": "Add technician",
  "team.dashboardAccess": "Dashboard access",
  "team.photo": "Photo",
  "team.revokeAccess": "Revoke dashboard access",

  // -------------------------------------------------------------- veículos
  "vehicles.addFirst": "Add vehicle",

  // ------------------------------------------------------------------ erro
  "error.offline": "We couldn't reach the server. Check your connection.",
  "image.choose": "Choose image",

  // ---------------------------------- acrescentado na revisão de idioma
  "import.intro": "Upload the list you already have — from Excel, from Google Contacts, or a plain list. We need <strong>{needs}</strong>; email, birthday and notes come along if they're there.",
  "import.changeFile": "Change file",
  "import.download": "Download template",
  "import.reading": "Reading the file…",
  "import.toImport": "to import",
  "import.noHeader": "The file has no header, so I read the columns in this order: <strong>{columns}</strong>. Check the sample below before importing.",
  "import.truncated": "The file is too large and I read only the first 5,000 rows. Import this part and upload the rest in another file.",
  "import.skipped": "Anyone already in your list is skipped — nothing is overwritten.",
  "import.sample": "Sample of what will come in",
  "import.problems": "Rows to check in your spreadsheet",
  "import.row": "Row {n}:",
  "import.andMore": "and {n} more…",
  "import.importN": "Import {n} customer(s)",
  "import.another": "Import another file",
  "import.csvHeader": "Name,Phone,Email,Birthdate,Notes",
  "import.csvRow1": "Jane Miller,(305) 555-0199,jane@example.com,04/03/1990,Prefers Saturday mornings",
  "import.csvRow2": "Bob Dyer,3055550147,,,",
  "modal.noCustomerFound": "No customer found. Add them below.",
  "modal.timeOffHint": "Nobody can book you in that window — not from the site, not from the front desk.",
  "banner.overdue": "Subscription past due.",
  "banner.fixNow": "Fix it now",
  "banner.readOnlyAfter": "After that the account goes read-only until the subscription is active again.",
  "banner.choosePlan": "Choose a plan",
  "customers.importCsv": "Import CSV",
  "profile.howYouAppear": "How you appear",
  "profile.bio": "Bio",
  "profile.payoutHint": "Used to pay your commission. Only you and the shop owner can see it — and every change is logged.",
  "auth.tempPasswordHint": "You signed in with a temporary password. Pick one only you know — it protects your customers' data.",
  "auth.emailExists": "You already have an account with this email. Confirm below and sign in with the password you already use — it stays the same.",
  "reports.completedOnly": "Only completed jobs count toward revenue.",
  "reports.commission": "Commission",
  "settings.readOnlyHint": "These are the house rules, set by the owner or manager. You can read them, but not change them.",
  "settings.savedNote": "In effect for the next bookings.",
  "site.viewMyPage": "View my page",
  "site.published": "Published on your site.",
  "sub.perMonth": "month",
  "sub.suspendedText": "Your schedule, your customers and your history are all still here — nothing was deleted. Pick a plan to get back in.",
  "sub.trialUntil": "You're on the free trial until",
  "sub.trialNotInterrupted": "— the trial isn't interrupted.",
  "sub.noLockIn": "No lock-in: cancel whenever you want, and access runs to the end of the period you paid for.",
  "sub.cancel": "Cancel subscription",
  "sub.cancelText": "If you cancel, you keep access until the end of the period you already paid for. Your data isn't deleted.",
  "team.limitReached": "You've hit the limit of",
  "team.servicesPerformed": "Services they perform",
  "team.needOneService": "Without at least one service, they don't show up when booking.",
  "team.signsInWith": "They sign in to the dashboard with",
  "team.seesOwnAgenda": "and see only their own schedule.",
  "team.revokeNote": "Revoking access doesn't delete the technician: they stay on the schedule, in the history and in the commissions.",
  "team.mustChange": ", and they'll have to change it on first sign-in.",
  "team.gotIt": "Got it, close",
  "team.loginExplains": "With a login, they see their own schedule on the phone, book their own customers and keep their own profile up to date.",
  "team.sendInvite": "Send an email invitation",
  "team.generatePassword": "Generate a password to hand over",
  "team.handPasswordHint": "Use the hand-over password when they don't usually open email. Either way they pick their own password on the first sign-in.",
  "vehicles.history": "History",
  "vehicles.newCustomer": "Add a new customer",
  "banner.overdueText": "Your schedule is still here and nothing was deleted, but new bookings are blocked{until}.",
  "banner.until": " until {date}",
  "banner.daysLeft": "{n} day(s) left",
  "banner.ofYourTrial": "of your {plan} trial.",
  "reports.from": "From",
  "reports.appointmentsShort": "Jobs",
  "reports.revenue": "Revenue",
  "reports.noShows": "No-shows",
  "reports.qty": "Qty.",
  "settings.granularityMinutes": "{n} minutes",
  "settings.slotStepHint": "How often the grid opens a slot. With a 15-minute step, a 40-minute job shows at 9:00, 9:15, 9:30 — not 9:00, 9:40.",
  "settings.reminder24h": "Reminder 24 hours before",
  "settings.reminder2h": "Reminder 2 hours before",
  "settings.reminderChannelHint": "Today the reminder goes out by email, and only to customers who left one. Sending over {channel} turns on once your shop's number is connected.",
  "sub.planActive": "Plan <strong>{plan}</strong> active",
  "sub.activeUntil": " until {date}",
  "sub.nextCharge": " Next charge of {amount} the following {cycle}.",
  "sub.bookingsBlocked": "New bookings are blocked.",
  "sub.trialFirstCharge": "If you subscribe now, the first charge only happens on",
  "sub.taxIdHint": "The account holder's {documento} is required to issue the invoice. If the shop has no company registration, use your own.",
  "sub.documentOk": "Document confirmed.",
  "sub.monthly": "Monthly",
  "sub.yearly": "Yearly",
  "sub.currentPlan": "current plan",
  "sub.yearlySavings": "saves {amount} a year",
  "sub.upToPros": "Up to {n} technicians.",
  "sub.whyLeavingPlaceholder": "helps us get better",
  "profile.pixKey": "Pix key",
  "profile.payoutTitle": "Payout details",
  "site.style": "Style",
  "team.signIn": "Sign in",
  "signup.haveAccount": "Already have an account?",
  "signup.slugPlaceholder": "joes-auto",
  "signup.slugFree": "{url} is available",
  "team.newShort": "New",
  "team.limitText": "You've hit the limit of <b>{n} technicians</b> on the {plan} plan. Upgrade to add more.",
  "team.credentialsReady": "All set. Hand these over —",
  "team.close": "Close",
  "vehicles.editVehicle": "Edit",
  "image.tooBig": "Image is {size} MB. The limit is 5 MB.",
  "common.undo": "Undo",
  "common.done": "Done",
  "common.date": "Date",
  "common.select": "Select…",
  "common.total": "Total:",
  "common.blocked": "Blocked",
  "common.editOf": "Edit {name}",
  "import.imported": "{n} customer(s) imported",
  "import.alreadyHad": "{n} were already on file. ",
  "import.leftOut": "{n} row(s) were left out for a missing name or a bad phone number.",
  "modal.bookFor": "Book · {amount}",
  "modal.day": "Day",
  "modal.startingAt": "Starting at",
  "modal.howLong": "How long",
  "boot.seePlans": "See plans and upgrade",
  "auth.goToLogin": "Go to sign in",
  "auth.invitedAs": "You've been invited as {name}, with your own schedule in the dashboard.",
  "profile.commissionOf": "{shop} · {n}% commission",
  "profile.pixKeyType": "Pix key type",
  "profile.payoutSaved": "Payout details saved.",
  "reports.appointments": "Jobs",
  "reports.booked": "{n} booked in the period",
  "reports.canceledCount": "{n} cancellation(s)",
  "services.offSite": "Not on the site",
  "services.deactivate": "Deactivate",
  "settings.gridAndWindows": "Grid and windows",
  "settings.slotStep": "Grid step",
  "settings.cancelWindow": "Cancellation window (hours)",
  "settings.rescheduleWindow": "Reschedule window (hours)",
  "settings.allowOverbook": "Allow squeezing people in",
  "settings.reminders": "Reminders",
  "site.identityTitle": "Identity",
  "site.logo": "Logo",
  "site.cover": "Cover image",
  "site.template": "Template",
  "site.primaryColor": "Main color",
  "site.primaryColorHint": "Background of the header.",
  "site.accentColor": "Accent color",
  "site.textsTitle": "Texts",
  "site.headline": "Headline",
  "site.subheadline": "Line below the headline",
  "site.socialTitle": "Social",
  "site.handlePlaceholder": "@yourshop",
  "site.handlePlain": "yourshop",
  "sub.paymentMethod": "Payment method",
  "sub.needTaxId": "Enter the {documento} above to unlock payment.",
  "sub.invoices": "Invoices",
  "sub.dueDate": "Due",
  "sub.amount": "Amount",
  "sub.pay": "Pay",
  "sub.receipt": "Receipt",
  "sub.canceledOn": "Subscription canceled on {date}. Access continues",
  "sub.resubscribe": ". To come back, just pick a plan above.",
  "team.accessOf": "{name}'s access",
  "team.inviteSentTo": "Invitation sent to",
  "team.inviteLinkHint": ". If the email is slow, send this link over {channel} — it's good for 72 hours.",
  "team.theirEmail": "Their email",
  "services.bufferSuffix": "(+{n} min buffer)",
  "sub.taxIdOptional": "The account holder's {documento} — optional. We only put it on the invoice when you have one.",
  "signup.earlyAccess": "Early access — you're joining as a beta tester.",
  "settings.comingSoon": "coming soon",
} as const;

export type Chave = keyof typeof en;

export const pt: Record<Chave, string> = {
  "gallery.title": "Galeria de fotos",
  "gallery.hint": "Mostre sua oficina, sua equipe e seus trabalhos. Adicione até 6 fotos e organize na ordem que preferir.",
  "gallery.limit": "Sua galeria pode ter até 6 fotos.",
  "gallery.formats": "Use imagens JPG, PNG ou WebP.",
  "gallery.size": "Cada foto pode ter até 5 MB.",
  "gallery.optional": "fotos · opcional",
  "gallery.add": "Adicionar fotos",
  "gallery.empty": "Comece pela fachada, pelo ambiente ou por um trabalho realizado. A galeria só aparece no site quando você adiciona uma foto.",
  "gallery.photo": "Foto",
  "gallery.up": "Mover foto para cima",
  "gallery.down": "Mover foto para baixo",
  "gallery.cover": "Usar como capa",
  "gallery.caption": "Legenda (opcional)",
  "gallery.saveCaption": "Salvar legenda",
  "gallery.saving": "Publicando fotos…",
  "gallery.autosave": "As alterações da galeria são publicadas na hora. JPG, PNG ou WebP, até 5 MB por foto.",
  "gallery.retry": "Tentar novamente",

  "nav.agenda": "Agenda",
  "nav.myAgenda": "Minha agenda",
  "nav.customers": "Clientes",
  "nav.vehicles": "Veículos",
  "nav.services": "Serviços",
  "nav.team": "Equipe",
  "nav.reports": "Relatórios",
  "nav.settings": "Configurações",
  "nav.site": "Página pública",
  "nav.subscription": "Assinatura",
  "nav.profile": "Meu perfil",
  "nav.signOut": "Sair",
  "nav.openMenu": "Abrir menu",
  "nav.closeMenu": "Fechar menu",

  "common.save": "Salvar",
  "common.saving": "Salvando…",
  "common.saved": "Salvo",
  "common.cancel": "Cancelar",
  "common.back": "Voltar",
  "common.continue": "Continuar",
  "common.confirm": "Confirmar",
  "common.delete": "Excluir",
  "common.edit": "Editar",
  "common.add": "Adicionar",
  "common.search": "Buscar",
  "common.loading": "Carregando…",
  "common.none": "Nenhum",
  "common.optional": "opcional",
  "common.required": "obrigatório",
  "common.yes": "Sim",
  "common.no": "Não",
  "common.close": "Fechar",
  "common.today": "Hoje",
  "common.name": "Nome",
  "common.email": "E-mail",
  "common.phone": "Telefone",
  "common.notes": "Observações",
  "common.active": "Ativo",
  "common.inactive": "Inativo",
  "common.error": "Algo deu errado. Tente de novo.",
  "common.empty": "Nada por aqui ainda.",

  "status.pending": "Aguardando",
  "status.confirmed": "Confirmado",
  "status.in_progress": "Atendendo",
  "status.completed": "Concluído",
  "status.canceled": "Cancelado",
  "status.no_show": "Não veio",

  "duration.minutesShort": "{n}min",
  "duration.hoursShort": "{h}h",
  "duration.hoursMinutes": "{h}h{m}",

  "agenda.title": "Agenda",
  "agenda.new": "Novo agendamento",
  "agenda.prevDay": "Dia anterior",
  "agenda.nextDay": "Próximo dia",
  "agenda.allTechs": "Todos os profissionais",
  "agenda.appointments": "Atendimentos",
  "agenda.expected": "Previsto no dia",
  "agenda.canceledNoShows": "Cancelados / faltas",
  "agenda.emptyTitle": "Nenhum agendamento neste dia",
  "agenda.emptyText": "Use o botão acima para marcar um cliente.",
  "agenda.noShowHistory": "{n} falta(s) no histórico",
  "agenda.mine": "Minha agenda",
  "agenda.team": "Equipe",
  "agenda.todayShort": "Hoje",
  "agenda.dayOff": "Bloquear horário",
  "agenda.nothingToday": "Nada marcado neste dia",
  "agenda.nothingTodayText": "Aproveite a folga — ou bloqueie o horário para ninguém marcar.",
  "agenda.viewDay": "Dia",
  "agenda.viewWeek": "Semana",
  "agenda.viewMonth": "Mês",
  "agenda.prevWeek": "Semana anterior",
  "agenda.nextWeek": "Próxima semana",
  "agenda.prevMonth": "Mês anterior",
  "agenda.nextMonth": "Próximo mês",
  "agenda.expectedPeriod": "Previsto no período",
  "agenda.openDay": "Abrir {day}",
  "agenda.morePlus": "+{n} mais",

  "mine.noProfileTitle": "Minha agenda",
  "mine.noProfileText":
    "Seu usuário ainda não está ligado a um perfil de profissional. Peça ao dono da oficina para fazer essa ligação em <b>Equipe</b>.",
  "mine.production": "Produção do dia",
  "mine.next": "Próximo",
  "mine.bookCustomer": "Marcar cliente",
  "mine.block": "Bloquear",
  "mine.freeDay": "Dia livre",
  "mine.freeDayText": "Nenhum atendimento marcado. Aproveite ou marque um cliente.",
  "mine.missedTimes": "Já faltou {n}×",

  "action.confirm": "Confirmar",
  "action.cancel": "Cancelar",
  "action.start": "Iniciar",
  "action.noShow": "Não veio",
  "action.complete": "Finalizar",

  "home.todayAppointments": "Atendimentos hoje",
  "home.todayBilled": "Faturado hoje",
  "home.onlyCompleted": "Só atendimentos concluídos",
  "home.monthBilled": "Faturado no mês",
  "home.monthNoShows": "Faltas no mês",
  "home.noShowHint": "Lembrete por WhatsApp reduz isso",
  "home.todayStatus": "Situação de hoje",
  "home.publicPage": "Página pública",
  "home.setupPending": "Termine de configurar",
  "home.setupHours": "horário de funcionamento",
  "home.setupServices": "serviços",
  "home.setupTeam": "quem atende",

  "home.hello": "Olá, {nome}",
  "home.onTrialOf": "Você está no período de teste do plano <b>{plano}</b>.",
  "home.monthAppointments": "{n} agendamentos",
  "home.publicPageText": "O link que seus clientes usam para agendar sozinhos.",
  "home.seeFullAgenda": "Ver agenda completa",
  "home.notAcceptingTitle": "Sua página ainda não aceita agendamento",
  "home.notAcceptingText": "Falta cadastrar: {itens}.",
  "home.finishSetup": "Terminar configuração",

  "auth.panelSubtitle": "Painel da oficina",
  "auth.password": "Senha",
  "auth.signIn": "Entrar",
  "auth.wrongCredentials": "E-mail ou senha incorretos.",
  "auth.newPasswordTitle": "Crie sua senha",
  "auth.newPassword": "Nova senha",
  "auth.minChars": "Pelo menos 8 caracteres.",
  "auth.minCharsPrivate": "Pelo menos 8 caracteres. Nem o dono da oficina vê esta senha.",
  "auth.inviteTitle": "Convite",
  "auth.inviteText": "Você foi convidado para acessar o painel desta oficina.",
  "auth.inviteUnavailable": "Convite indisponível",
  "auth.inviteUsed": "Este convite já foi usado. Se a conta é sua, é só entrar normalmente.",
  "auth.inviteExpired": "Este link expirou ou não existe mais. Peça um novo ao dono da oficina.",
  "auth.fullName": "Seu nome completo",
  "auth.createPassword": "Crie uma senha",
  "auth.acceptInvite": "Aceitar convite",
  "auth.creatingAccess": "Criando meu acesso…",

  "signup.subtitle": "Crie sua conta em 1 minuto",
  "signup.chosenPlan": "Plano escolhido",
  "signup.trialNote": "3 meses grátis. Sem cartão de crédito agora.",
  "signup.upTo": "até {n} profissionais",
  "signup.shopName": "Nome da oficina",
  "signup.shopNamePlaceholder": "Oficina do Zé",
  "signup.pageAddress": "Endereço da sua página",
  "signup.yourName": "Seu nome",
  "signup.passwordHint": "Mínimo de 10 caracteres.",
  "signup.submit": "Criar minha oficina",
  "signup.failed": "Não foi possível criar a conta. Tente novamente.",

  "setup.eyebrow": "Configuração",
  "setup.title": "Vamos preparar sua oficina",
  "setup.doneTitle": "Sua página já aceita agendamento.",
  "setup.intro": "Três passos e seus clientes já conseguem agendar sozinhos.",
  "setup.team": "Quem atende",
  "setup.hours": "Horário de funcionamento",
  "setup.services": "Seus serviços",
  "setup.whenOpen": "Quando a oficina abre?",
  "setup.whatOffer": "O que você oferece?",
  "setup.displayName": "Nome que aparece para o cliente",
  "setup.finish": "Concluir configuração",
  "setup.live": "Sua página está no ar",

  "setup.allSet": "Tudo pronto!",
  "setup.whoWorks": "Quem atende?",
  "setup.hoursHint": "Já deixamos o mais comum preenchido. Ajuste o que for diferente.",
  "setup.servicesHint":
    "Marque o que a casa faz e ajuste o preço. Os valores são média de mercado — quase ninguém cobra a média. Duração e buffers ficam no painel.",
  "setup.teamHint":
    "Comece por você. Depois adiciona a equipe toda em <b>Equipe</b>, com comissão e login para cada um.",
  "setup.willRun": "Vai executar os {n} serviços cadastrados. Dá para mudar isso a qualquer momento.",
  "setup.priceOf": "Preço de {servico}",
  "setup.liveHint":
    "Cole este link na bio do Instagram e no Google. É por aqui que seus clientes agendam sozinhos, 24h por dia.",

  "services.emptyTitle": "Nenhum serviço cadastrado",
  "services.emptyText":
    "Cadastre troca de óleo, lavagem, revisão e o que mais a casa oferecer.",
  "services.addOne": "Cadastrar serviço",
  "services.edit": "Editar serviço",
  "services.new": "Novo serviço",
  "services.duration": "Duração (min)",
  "services.price": "Preço ({moeda})",
  "services.bufferBefore": "Antes (min)",
  "services.bufferAfter": "Depois (min)",
  "services.requiresVehicle": "Exige a placa do veículo",

  "customers.search": "Buscar por nome ou telefone",
  "customers.noneFound": "Nenhum cliente encontrado",
  "customers.noneYet": "Nenhum cliente ainda",
  "customers.tryAnother": "Tente outro nome ou telefone.",
  "customers.emptyText":
    "Cadastre o primeiro, traga sua lista de uma vez, ou deixe que eles apareçam conforme você agenda.",

  "vehicles.search": "Placa, marca, modelo ou dono",
  "vehicles.noneFound": "Nenhum veículo encontrado",
  "vehicles.noneYet": "Nenhum veículo ainda",
  "vehicles.tryPlate": "Tente a placa sem o hífen, ou o nome do dono.",
  "vehicles.emptyText":
    "Todo agendamento com placa cadastra o carro sozinho. Aqui você completa a ficha.",
  "vehicles.edit": "Editar veículo",
  "vehicles.new": "Novo veículo",
  "vehicles.owner": "Dono",
  "vehicles.pickCustomer": "Selecione o cliente",
  "vehicles.make": "Marca",
  "vehicles.model": "Modelo",
  "vehicles.year": "Ano",
  "vehicles.color": "Cor",
  "vehicles.odometer": "Quilometragem",
  "vehicles.odometerHint": "O número que diz se a revisão está vencida.",
  "vehicles.type": "Tipo",
  "vehicles.fuel": "Combustível",
  "vehicles.notesPlaceholder": "Ex.: já teve retífica de motor em 2023",
  "vehicles.noHistory": "Nada registrado ainda",
  "vehicles.noHistoryText":
    "O histórico começa no primeiro atendimento concluído neste veículo.",

  "services.subtitle": "Duração e preço que aparecem para o cliente.",
  "services.buffers": "Buffers",
  "services.buffersHint":
    "Tempo entre atendimentos para tirar o carro do elevador, lavar a mão e receber. Sem isso a agenda fica otimista e o profissional atrasa.",
  "services.onlineBookable": "Disponível para agendamento no site público",
  "services.requiresVehicleHint":
    "Deixe ligado em tudo que entra no pátio. Desligue só no que não tem carro — orçamento por telefone, venda de peça no balcão.",
  "services.kind": "Tipo",
  "services.kindService": "Serviço",
  "services.kindQuote": "Orçamento",
  "services.kindServiceHint": "Trabalho feito no carro, pelo preço que você definir.",
  "services.kindQuoteHint":
    "A visita em que o cliente vem descobrir o preço. Não cobra — defina quanto tempo sua casa leva para olhar o carro, e escreva o valor no próprio atendimento.",
  "services.quote": "Orçamento",
  "services.noCharge": "Sem cobrança",

  // ------------------------------------------------- ficha do atendimento
  "detail.title": "Detalhes do atendimento",
  "detail.open": "Abrir o atendimento de {name}",
  "detail.quote": "Orçamento",
  "detail.notQuotedYet": "Sem valor",
  "detail.customerNotes": "O que o cliente pediu",
  "detail.quotedPrice": "Valor orçado",
  "detail.quotedPriceHint":
    "O que você disse ao cliente que vai custar o reparo. Fica fora do previsto do dia até o serviço ser marcado de verdade.",
  "detail.report": "Laudo do veículo",
  "detail.reportHint": "O que você viu no carro. É o que se relê quando ele voltar.",
  "detail.reportHintQuote":
    "Descreva o carro: o que está danificado, o que precisa trocar, o que pode esperar. É isso que o cliente veio buscar.",
  "detail.internalNotes": "Anotação interna",
  "detail.internalNotesHint": "Só para a casa — o cliente nunca vê.",
  "detail.saved": "Salvo",
  "detail.history": "Histórico",
  "detail.noHistory": "Nada registrado ainda.",

  "photos.title": "Vistoria do veículo",
  "photos.hint":
    "Como o carro chegou, e como ele saiu. É o que resolve o \u201cesse risco já estava aí\u201d. Só a sua equipe enxerga.",
  "photos.arrival": "Entrada",
  "photos.departure": "Saída",
  "photos.add": "Tirar foto",
  "photos.full": "Limite atingido",
  "photos.remove": "Apagar foto",
  "photos.format": "Use uma imagem JPG, PNG ou WebP.",
  "photos.failed": "Não consegui carregar esta foto.",

  "event.created": "criou",
  "event.confirmed": "confirmou",
  "event.started": "iniciou",
  "event.completed": "concluiu",
  "event.canceled": "cancelou",
  "event.noShow": "marcou falta",
  "event.rescheduled": "reagendou",
  "event.quoted": "alterou o valor orçado",
  "event.photoAdded": "adicionou uma foto",
  "event.photoRemoved": "apagou uma foto",

  "vehicles.fleetCount": "{n} veículo(s) na frota da casa",
  "vtype.car": "Carro",
  "vtype.suv": "SUV / Caminhonete",
  "vtype.van": "Van / Utilitário",
  "vtype.motorcycle": "Moto",
  "vtype.truck": "Caminhão",
  "vtype.other": "Outro",
  "fuel.none": "Não informado",
  "fuel.flex": "Flex",
  "fuel.gasoline": "Gasolina",
  "fuel.ethanol": "Etanol",
  "fuel.diesel": "Diesel",
  "fuel.cng": "GNV",
  "fuel.hybrid": "Híbrido",
  "fuel.electric": "Elétrico",

  "settings.loadFailed": "Não foi possível carregar as regras da agenda.",
  "settings.subtitle":
    "Como sua casa aceita e organiza reservas. Vale para o site, o WhatsApp e o painel.",
  "settings.slotsHint": "Quais horários o cliente vê e até quando ele pode se mexer.",
  "settings.minAdvance": "Antecedência mínima (minutos)",
  "settings.minAdvanceHint": "Impede a reserva para \u201Cdaqui a 5 minutos\u201D. Zero libera o próximo horário.",
  "settings.horizonHint": "Até quando o cliente consegue marcar. Prazo longo aumenta a falta.",
  "settings.cancelHint":
    "Abaixo disso o cliente não cancela sozinho: a página mostra o WhatsApp da casa.",
  "settings.rescheduleHint":
    "Mesma ideia para trocar o horário. Você e a recepção não têm esse limite.",
  "settings.sameDay": "Aceitar reserva para o mesmo dia",
  "settings.sameDayHint": "Desligado, o cliente só consegue marcar de amanhã em diante.",
  "settings.autoConfirmHint":
    "Desligado, cada reserva nasce pendente e espera o seu \u201CConfirmar\u201D na agenda — dá para conferir cliente novo antes de segurar o box.",
  "settings.overbookHint":
    "Libera marcar fora da grade: fora do horário de funcionamento ou sem respeitar a antecedência. Dois clientes no mesmo horário continuam impossíveis.",
  "settings.teamBehavior": "Comportamento da equipe",
  "settings.teamBehaviorHint": "As duas exceções que cada oficina pede de um jeito diferente.",
  "settings.remindersHint": "O que o cliente recebe sem você fazer nada.",
  "settings.reminder24Hint":
    "Reserva feita com menos de 24h de antecedência não recebe este — só o de 2h.",
  "settings.reminder2Hint":
    "É o que mais reduz falta. Desligue só se a casa avisa por conta.",
  "settings.saveRules": "Salvar regras",

  "team.withLogin": "Com login",
  "team.inviteSent": "Convite enviado",
  "team.noLogin": "Sem login",
  "reports.sevenDays": "7 dias",
  "team.emptyTitle": "Nenhum profissional cadastrado",
  "team.edit": "Editar profissional",
  "team.new": "Novo profissional",
  "team.commission": "Comissão (%)",
  "team.commissionLocked": "Só o dono da oficina altera a comissão.",
  "team.agendaColor": "Cor na agenda",
  "team.taxIdInvalid": "{documento} inválido. Confira os números.",
  "team.taxIdHint": "Necessário para o repasse. Ele mesmo pode preencher depois, em Meu perfil.",
  "team.photoHint": "Aparece na equipe do seu site. Rosto centralizado funciona melhor.",
  "team.bioPlaceholder": "Ex.: Especialista em injeção eletrônica e câmbio automático.",
  "team.shownOnce": "não aparece de novo",

  "reports.thisMonth": "Este mês",
  "reports.lastMonth": "Mês passado",
  "reports.loadFailed": "Não foi possível carregar o relatório",
  "reports.checkPeriod": "Confira o período escolhido e tente de novo.",
  "reports.avgTicket": "Ticket médio",
  "reports.perAppointment": "Por atendimento, não por serviço",
  "reports.noneCompleted": "Nenhum atendimento concluído no período",
  "reports.noneCompletedHint":
    "O faturamento conta a partir do momento em que você finaliza o atendimento na agenda.",
  "reports.byTech": "Por profissional",
  "reports.byTechHint": "Quem produziu no período",
  "reports.byService": "Por serviço",
  "reports.byServiceHint": "O que a casa mais vende",

  "site.loadFailed": "Não foi possível carregar a identidade do site.",
  "site.title": "A cara da sua oficina",
  "site.subtitle": "O que você mudar aqui aparece na sua página pública.",
  "site.identity": "Nome, logo e a foto grande do topo.",
  "site.logoHint": "Aparece no topo do site. PNG com fundo transparente fica melhor.",
  "site.coverHint":
    "O fundo do topo. Horizontal e larga — é ela que vai no card do WhatsApp.",
  "site.buttonsLinks": "Botões e links.",
  "site.displayFont": "Fonte dos títulos",
  "site.beforeBooking": "O que o cliente lê antes de agendar.",
  "site.headlineHint": "Vazio usa o nome da oficina.",
  "site.subheadlineHint": "Vazio usa a frase padrão.",
  "site.subheadlinePlaceholder":
    "Escolha o serviço, o profissional e o horário. Leva menos de um minuto e não precisa criar conta.",
  "site.about": "Sobre a oficina",
  "site.aboutPlaceholder":
    "Ex.: Desde 1998 no bairro. Diagnóstico honesto, peça original e orçamento antes de mexer.",
  "site.whatToShow": "O que mostrar",
  "site.showPrices": "Mostrar preços",
  "site.showPricesHint": "Desligado, o cliente ainda escolhe o serviço — só não vê o valor.",
  "site.showTeam": "Mostrar fotos da equipe",
  "site.showTeamHint": "As fotos são cadastradas em Equipe, no perfil de cada profissional.",
  "site.socialHint": "Viram links no rodapé do site.",
  "site.savePublish": "Salvar e publicar",

  "sub.ownerOnly": "Só o dono da oficina pode ver e alterar a assinatura.",
  "sub.loadFailed": "Não foi possível carregar sua assinatura. Tente de novo em instantes.",
  "sub.canceledNote": "Cancelada. O acesso continua até o fim do período já pago.",
  "sub.taxIdInvalid": "Confira os números: precisa ser um {documento} válido.",
  "sub.billingData": "Dados de cobrança",
  "sub.creditCard": "Cartão de crédito",
  "sub.trialLeft": "Teste grátis — faltam {n} dia(s), sem cartão cadastrado.",
  "sub.active": "Ativa.",
  "profile.pixPhone": "Celular",
  "sub.choosePlan": "Escolha seu plano",
  "sub.updateBilling": "Atualizar cobrança",
  "sub.status": "Situação",
  "sub.whyLeaving": "O que motivou? (opcional)",

  "profile.loadFailed": "Não foi possível carregar seu cadastro. Tente de novo em instantes.",
  "profile.photoHint":
    "Aparece na equipe do site da oficina. Rosto centralizado funciona melhor.",
  "profile.displayName": "Nome de exibição",
  "profile.notSet": "Não informado",
  "profile.currentPassword": "Senha atual",
  "profile.changePassword": "Trocar senha",
  "profile.passwordChanged": "Senha trocada.",
  "profile.randomKey": "Chave aleatória",

  "modal.editCustomer": "Editar cliente",
  "modal.newCustomer": "Novo cliente",
  "modal.fullNamePlaceholder": "Nome e sobrenome",
  "modal.phoneHint": "É por aqui que o lembrete de agendamento chega.",
  "modal.notesOptional": "Observações (opcional)",
  "modal.notesPlaceholder": "Ex.: prefere ser avisado à tarde",
  "modal.imageSelected": "Selecionada — salve para publicar.",
  "modal.alreadyThere": "já cadastrados",
  "modal.withProblems": "com problema",
  "modal.nothingNew": "Nada novo para importar",
  "modal.services": "Serviços",
  "modal.findCustomer": "Buscar cliente",
  "modal.nameOrPhone": "Nome ou telefone",
  "modal.whichVehicle": "Qual veículo?",
  "modal.otherPlate": "Ou outra placa",
  "modal.newPlateHint": "Placa nova cadastra o carro junto com o agendamento.",
  "modal.trialEndsToday": "Seu teste termina hoje.",
  "modal.blockTime": "Bloquear horário",
  "modal.reasonOptional": "Motivo (opcional)",
  "modal.noReason": "Sem motivo",
  "modal.lunch": "Almoço",
  "modal.personal": "Compromisso pessoal",
  "modal.doctor": "Consulta médica",
  "modal.dayOff": "Folga",
  "modal.repeatNewPassword": "Repita a nova senha",
  "modal.passwordsDiffer": "As senhas não são iguais.",
  "modal.saveAndEnter": "Salvar e entrar",

  "role.owner": "Proprietário",
  "role.manager": "Gerente",
  "role.receptionist": "Recepção",
  "role.professional": "Profissional",

  "nav.home": "Início",
  "nav.houseAgenda": "Agenda da casa",
  "nav.mySite": "Meu site",
  "nav.scheduleRules": "Regras da agenda",
  "nav.noPlan": "Sem plano",
  "nav.onTrial": " · em teste",

  "boot.suspendedTitle": "Conta suspensa",
  "boot.suspendedText":
    "A assinatura não foi regularizada e o acesso ao painel está bloqueado. Seus dados continuam guardados: agenda, clientes e histórico voltam como estavam assim que o pagamento for confirmado.",
  "boot.noGarageTitle": "Conta sem oficina",
  "boot.noGarageText":
    "Seu acesso existe, mas ainda não está vinculado a nenhuma oficina ativa. Peça ao dono da casa para enviar um convite, ou crie a sua.",
  "boot.failedTitle": "Não foi possível carregar sua conta",
  "boot.failedText":
    "O servidor não respondeu como esperado. Verifique sua conexão e tente de novo.",
  "boot.reactivate": "Reativar conta",
  "boot.retry": "Tentar de novo",
  // ------------------------------------------ configuração (continuação)
  "hours.title": "Horário de funcionamento",
  "hours.hint": "As horas em que a oficina recebe carro. Sua página só oferece horário dentro delas.",
  "hours.loadFailed": "Não foi possível carregar o horário de funcionamento.",
  "hours.timeOffHint": "Fechar num dia só — feriado, viagem — é bloqueio na agenda, não mudança aqui.",
  "hours.needOneDay": "Deixe pelo menos um dia aberto. Para fechar por um período, bloqueie as datas na agenda.",
  "hours.save": "Salvar horário",
  "hours.saved": "Salvo. Sua página já oferece o novo horário.",
  "hours.startOf": "{dia} — abre às",
  "hours.endOf": "{dia} — fecha às",
  "hours.closed": "Fechado",
  "hours.to": "até",
  "hours.removeRange": "remover",
  "hours.addBreak": "+ intervalo (almoço)",
  "setup.saveContinue": "Salvar e continuar",
  "setup.addServices": "Adicionar {n} serviço(s)",
  "setup.laterPrefix": "Pode fazer isso depois —",
  "setup.laterLink": "ir para o painel",
  "setup.copy": "Copiar",
  "setup.copied": "Copiado",
  "setup.viewPage": "Ver minha página",
  "setup.goDashboard": "Ir para o painel",
  "setup.namePlaceholder": "Zé",

  // ------------------------------------------------------------ importação
  "import.title": "Importar clientes",
  "import.pickFile": "Escolher arquivo",
  "import.templateName": "modelo-clientes.csv",
  "import.columns": "nome, telefone, e-mail, nascimento, observações",
  "import.needs": "nome e telefone com DDD",

  // ----------------------------------------------------------- agendamento
  "modal.newAppointment": "Novo agendamento",
  "modal.technician": "Profissional",
  "modal.orCreateNow": "Ou cadastre na hora",
  "modal.plate": "Placa",
  "modal.restOfDay": "Resto do dia",
  "modal.wholeDay": "Dia inteiro",

  // ---------------------------------------------------------------- perfil
  "profile.title": "Meu perfil",
  "profile.noProfileText":
    "Sua conta não está ligada a um perfil de profissional, então não há agenda nem comissão própria. Você ainda pode trocar sua senha aqui.",
  "profile.photo": "Foto",
  "profile.saved": "Cadastro salvo.",

  // -------------------------------------------------------------- clientes
  "customers.title": "Clientes",

  // ------------------------------------------------------------ relatórios
  "reports.to": "Até",
  "reports.byTechnician": "Por profissional",

  // ---------------------------------------------------------- configurações
  "settings.bookingHorizon": "Horizonte de reserva (dias)",
  "settings.autoConfirm": "Confirmar reservas automaticamente",
  "settings.seeAllAgendas": "Profissionais veem todas as agendas",
  "settings.seeAllAgendasHint":
    "Desligado, cada profissional vê só os clientes dele. Ligado, todos veem a casa inteira.",
  "settings.selfBooking": "Profissionais podem se encaixar",
  "settings.selfBookingHint":
    "Deixa o profissional marcar na própria agenda fora da grade — o cliente de sempre que aparece sem hora marcada.",

  // ------------------------------------------------------------- meu site
  "site.pageTitle": "Meu site",
  "site.lookHint": "Escolha o visual e as cores da casa.",
  "site.visibilityHint": "Nem toda casa quer tudo exposto.",

  // ----------------------------------------------------------- assinatura
  "sub.title": "Assinatura",
  "sub.backToDashboard": "Voltar ao painel",
  "sub.perYear": "ano",
  "sub.suspended": "Conta suspensa.",
  "sub.subscribe": "Assinar",
  "sub.billing": "Cobrança",

  // ---------------------------------------------------------------- equipe
  "team.emptyText": "Cadastre os profissionais — eles podem ter login depois.",
  "team.addFirst": "Cadastrar profissional",
  "team.dashboardAccess": "Acesso ao painel",
  "team.photo": "Foto",
  "team.revokeAccess": "Tirar acesso ao painel",

  // -------------------------------------------------------------- veículos
  "vehicles.addFirst": "Cadastrar veículo",

  // ------------------------------------------------------------------ erro
  "error.offline": "Não foi possível falar com o servidor. Verifique sua conexão.",
  "image.choose": "Escolher imagem",

  // ---------------------------------- acrescentado na revisão de idioma
  "import.intro": "Suba a planilha que você já tem — do Excel, do Google Contatos ou uma lista simples. Precisamos de <strong>{needs}</strong>; e-mail, nascimento e observações entram se estiverem lá.",
  "import.changeFile": "Trocar arquivo",
  "import.download": "Baixar modelo",
  "import.reading": "Lendo o arquivo…",
  "import.toImport": "a importar",
  "import.noHeader": "O arquivo não tem cabeçalho, então li as colunas nesta ordem: <strong>{columns}</strong>. Confira a amostra abaixo antes de importar.",
  "import.truncated": "O arquivo é grande demais e li só as primeiras 5.000 linhas. Importe esta parte e depois suba o restante em outro arquivo.",
  "import.skipped": "Quem já está na sua base é pulado — nada é sobrescrito.",
  "import.sample": "Amostra do que vai entrar",
  "import.problems": "Linhas para você conferir na planilha",
  "import.row": "Linha {n}:",
  "import.andMore": "e mais {n}…",
  "import.importN": "Importar {n} cliente(s)",
  "import.another": "Importar outro arquivo",
  "import.csvHeader": "Nome,Telefone,E-mail,Data de Nascimento,Observações",
  "import.csvRow1": "Ana Paula,(11) 98888-7777,ana@exemplo.com,03/04/1990,Prefere sábado de manhã",
  "import.csvRow2": "Bruno Dias,11977776666,,,",
  "modal.noCustomerFound": "Nenhum cliente encontrado. Cadastre abaixo.",
  "modal.timeOffHint": "Ninguém consegue agendar com você nesse período — nem pelo site, nem pela recepção.",
  "banner.overdue": "Assinatura vencida.",
  "banner.fixNow": "Regularizar agora",
  "banner.readOnlyAfter": "Depois disso a conta fica só para consulta até a assinatura ser ativada.",
  "banner.choosePlan": "Escolher plano",
  "customers.importCsv": "Importar CSV",
  "profile.howYouAppear": "Como você aparece",
  "profile.bio": "Apresentação",
  "profile.payoutHint": "Usados para pagar sua comissão. Só você e o dono da oficina veem — e toda alteração fica registrada.",
  "auth.tempPasswordHint": "Você entrou com uma senha provisória. Escolha uma que só você saiba — ela protege os dados dos seus clientes.",
  "auth.emailExists": "Você já tem uma conta com este e-mail. Confirme abaixo e entre com a senha que já usa — ela continua a mesma.",
  "reports.completedOnly": "Só atendimentos concluídos entram no faturamento.",
  "reports.commission": "Comissão",
  "settings.readOnlyHint": "Estas são as regras da casa, definidas pelo dono ou gerente. Você pode consultar, mas não alterar.",
  "settings.savedNote": "Valendo para as próximas reservas.",
  "site.viewMyPage": "Ver minha página",
  "site.published": "Publicado no seu site.",
  "sub.perMonth": "mês",
  "sub.suspendedText": "Sua agenda, seus clientes e seu histórico continuam guardados — nada foi apagado. Escolha um plano para voltar.",
  "sub.trialUntil": "Você está no teste grátis até",
  "sub.trialNotInterrupted": "— o teste não é interrompido.",
  "sub.noLockIn": "Sem fidelidade: dá para cancelar quando quiser, e o acesso vai até o fim do período pago.",
  "sub.cancel": "Cancelar assinatura",
  "sub.cancelText": "Ao cancelar, você continua com acesso até o fim do período já pago. Seus dados não são apagados.",
  "team.limitReached": "Você atingiu o limite de",
  "team.servicesPerformed": "Serviços que executa",
  "team.needOneService": "Sem pelo menos um serviço, ele não aparece na hora de agendar.",
  "team.signsInWith": "Ele entra no painel com",
  "team.seesOwnAgenda": "e vê somente a própria agenda.",
  "team.revokeNote": "Tirar o acesso não apaga o profissional: ele continua na agenda, no histórico e nas comissões.",
  "team.mustChange": ", e ele terá que trocá-la no primeiro acesso.",
  "team.gotIt": "Anotei, fechar",
  "team.loginExplains": "Com login, ele vê a própria agenda no celular, marca clientes dele e mantém o próprio cadastro em dia.",
  "team.sendInvite": "Enviar convite por e-mail",
  "team.generatePassword": "Gerar senha para entregar na mão",
  "team.handPasswordHint": "Use a senha na mão quando ele não costuma abrir e-mail. Nos dois casos ele escolhe a senha dele no primeiro acesso.",
  "vehicles.history": "Histórico",
  "vehicles.newCustomer": "Cadastrar um cliente novo",
  "banner.overdueText": "Sua agenda continua aqui e nada foi apagado, mas novos agendamentos estão bloqueados{until}.",
  "banner.until": " até {date}",
  "banner.daysLeft": "Faltam {n} dia(s)",
  "banner.ofYourTrial": "do seu teste do plano {plan}.",
  "reports.from": "De",
  "reports.appointmentsShort": "Atend.",
  "reports.revenue": "Faturamento",
  "reports.noShows": "Faltas",
  "reports.qty": "Qtd.",
  "settings.granularityMinutes": "{n} minutos",
  "settings.slotStepHint": "De quanto em quanto tempo a grade abre horário. Com passo de 15, um serviço de 40 min aparece às 09:00, 09:15, 09:30 — e não 09:00, 09:40.",
  "settings.reminder24h": "Lembrete 24 horas antes",
  "settings.reminder2h": "Lembrete 2 horas antes",
  "settings.reminderChannelHint": "Hoje o lembrete sai por e-mail, e só para o cliente que deixou e-mail no cadastro. O envio por {channel} entra quando o número da sua casa estiver ligado.",
  "sub.planActive": "Plano <strong>{plan}</strong> ativo",
  "sub.activeUntil": " até {date}",
  "sub.nextCharge": " Próxima cobrança de {amount} no {cycle} seguinte.",
  "sub.bookingsBlocked": "Novos agendamentos bloqueados.",
  "sub.trialFirstCharge": "Se assinar agora, a primeira cobrança só acontece em",
  "sub.taxIdHint": "O {documento} do titular é exigido para emitir a cobrança e a nota fiscal. Se a oficina não tem CNPJ, use o seu CPF.",
  "sub.documentOk": "Documento confirmado.",
  "sub.monthly": "Mensal",
  "sub.yearly": "Anual",
  "sub.currentPlan": "plano atual",
  "sub.yearlySavings": "economia de {amount} por ano",
  "sub.upToPros": "Até {n} profissionais.",
  "sub.whyLeavingPlaceholder": "ajuda a gente a melhorar",
  "profile.pixKey": "Chave Pix",
  "profile.payoutTitle": "Dados para repasse",
  "site.style": "Estilo",
  "team.signIn": "Entrar",
  "signup.haveAccount": "Já tem conta?",
  "signup.slugPlaceholder": "oficina-do-ze",
  "signup.slugFree": "{url} está livre",
  "team.newShort": "Novo",
  "team.limitText": "Você atingiu o limite de <b>{n} profissionais</b> do plano {plan}. Faça upgrade para adicionar mais.",
  "team.credentialsReady": "Pronto. Entregue estes dados para ele — a senha",
  "team.close": "Fechar",
  "vehicles.editVehicle": "Editar",
  "image.tooBig": "Imagem de {size} MB. O limite é 5 MB.",
  "common.undo": "Desfazer",
  "common.done": "Concluir",
  "common.date": "Data",
  "common.select": "Selecione…",
  "common.total": "Total:",
  "common.blocked": "Bloqueado",
  "common.editOf": "Editar {name}",
  "import.imported": "{n} cliente(s) importado(s)",
  "import.alreadyHad": "{n} já estavam na base. ",
  "import.leftOut": "{n} linha(s) ficaram de fora por falta de nome ou telefone válido.",
  "modal.bookFor": "Agendar · {amount}",
  "modal.day": "Dia",
  "modal.startingAt": "A partir de",
  "modal.howLong": "Por quanto tempo",
  "boot.seePlans": "Ver planos e fazer upgrade",
  "auth.goToLogin": "Ir para o login",
  "auth.invitedAs": "Você foi convidado como {name}, com agenda própria no painel.",
  "profile.commissionOf": "{shop} · comissão de {n}%",
  "profile.pixKeyType": "Tipo da chave Pix",
  "profile.payoutSaved": "Dados de repasse salvos.",
  "reports.appointments": "Atendimentos",
  "reports.booked": "{n} agendados no período",
  "reports.canceledCount": "{n} cancelamento(s)",
  "services.offSite": "Fora do site",
  "services.deactivate": "Desativar",
  "settings.gridAndWindows": "Grade e janelas",
  "settings.slotStep": "Passo da grade",
  "settings.cancelWindow": "Janela de cancelamento (horas)",
  "settings.rescheduleWindow": "Janela de reagendamento (horas)",
  "settings.allowOverbook": "Permitir encaixe na casa",
  "settings.reminders": "Lembretes",
  "site.identityTitle": "Identidade",
  "site.logo": "Logo",
  "site.cover": "Imagem de capa",
  "site.template": "Template",
  "site.primaryColor": "Cor principal",
  "site.primaryColorHint": "Fundo do topo.",
  "site.accentColor": "Cor de destaque",
  "site.textsTitle": "Textos",
  "site.headline": "Chamada do topo",
  "site.subheadline": "Frase do topo",
  "site.socialTitle": "Redes sociais",
  "site.handlePlaceholder": "@suaoficina",
  "site.handlePlain": "suaoficina",
  "sub.paymentMethod": "Forma de pagamento",
  "sub.needTaxId": "Informe o {documento} acima para liberar o pagamento.",
  "sub.invoices": "Faturas",
  "sub.dueDate": "Vencimento",
  "sub.amount": "Valor",
  "sub.pay": "Pagar",
  "sub.receipt": "Comprovante",
  "sub.canceledOn": "Assinatura cancelada em {date}. O acesso segue",
  "sub.resubscribe": ". Para voltar, é só assinar um plano acima.",
  "team.accessOf": "Acesso de {name}",
  "team.inviteSentTo": "Convite enviado para",
  "team.inviteLinkHint": ". Se o e-mail demorar, mande este link por {channel} — vale por 72 horas.",
  "team.theirEmail": "E-mail dele",
  "services.bufferSuffix": "(+{n}min de buffer)",
  "sub.taxIdOptional": "O {documento} do titular — opcional. Só entra na nota quando você tem um.",
  "signup.earlyAccess": "Early access — você entra como beta tester.",
  "settings.comingSoon": "em breve",
};

const DICIONARIOS: Record<string, Record<Chave, string>> = {
  "en-us": en as unknown as Record<Chave, string>,
  "pt-br": pt,
};

export const IDIOMAS = ["en-us", "pt-br"] as const;
export type Idioma = (typeof IDIOMAS)[number];

/** "pt-BR", "pt", "pt_br" → "pt-br". Desconhecido → `null`.
 *
 * O casamento por prefixo existe por causa do `navigator.language`, que
 * manda "pt", "pt-PT" ou "en-GB" — nenhum deles é um código nosso. */
export function normalizar(valor: string | null | undefined): Idioma | null {
  if (!valor) return null;
  const limpo = valor.trim().replace("_", "-").toLowerCase();
  const exato = IDIOMAS.find((i) => i === limpo);
  if (exato) return exato;
  const base = limpo.split("-")[0];
  return IDIOMAS.find((i) => i.split("-")[0] === base) ?? null;
}

/** Chave do idioma escolhido, no `localStorage` deste host.
 *
 * `localStorage` e não cookie: o cookie da landing é host-only de
 * propósito (senão a escolha do visitante sobrescreveria o idioma da
 * página de uma oficina), então ele não chega aqui. O que chega é o
 * `?lang=` do link — e é essa escolha que precisa sobreviver à navegação
 * dentro do painel. */
const CHAVE_IDIOMA = "gc_lang";

/** O idioma das telas anteriores ao login, em ordem de autoridade.
 *
 * 1. `?lang=` na URL — quem clicou na landing em inglês pediu inglês.
 * 2. O que já foi escolhido neste host antes.
 * 3. O navegador.
 *
 * O passo 1 é a ponte entre as duas superfícies: `garagem.club` e
 * `app.garagem.club` são hosts diferentes e não compartilham cookie.
 * Sem ele, quem lia a landing em inglês caía num cadastro em português —
 * e concluía, com razão, que o produto não é para ele. */
export function idiomaInicial(): Idioma {
  try {
    const daUrl = normalizar(new URLSearchParams(window.location.search).get("lang"));
    if (daUrl) {
      window.localStorage.setItem(CHAVE_IDIOMA, daUrl);
      return daUrl;
    }
    const guardado = normalizar(window.localStorage.getItem(CHAVE_IDIOMA));
    if (guardado) return guardado;
  } catch {
    // Navegador com storage bloqueado (aba anônima, política do
    // dispositivo). O idioma do navegador ainda serve.
  }
  return idiomaDoNavegador();
}

/** O idioma do navegador, para as telas anteriores ao login.
 *
 * Login e cadastro não têm `me` — e é justamente ali que o idioma errado
 * custa mais caro: quem não entende a tela de cadastro não vira cliente. */
export function idiomaDoNavegador(): Idioma {
  const lista = typeof navigator === "undefined" ? [] : (navigator.languages ?? [navigator.language]);
  for (const candidato of lista) {
    const achado = normalizar(candidato);
    if (achado) return achado;
  }
  return "en-us";
}

type Vars = Record<string, string | number>;

export function traduzir(idioma: Idioma, chave: Chave, vars?: Vars): string {
  const texto = (DICIONARIOS[idioma] ?? en)[chave] ?? en[chave] ?? chave;
  if (!vars) return texto;
  return Object.entries(vars).reduce(
    (acc, [nome, valor]) => acc.replaceAll(`{${nome}}`, String(valor)),
    texto,
  );
}

interface Contexto {
  idioma: Idioma;
  /** Moeda da oficina — a que ELA cobra dos clientes dela. */
  moeda: string;
}

/** O default do contexto é o idioma do NAVEGADOR, não o inglês fixo.
 *
 * Login, cadastro e aceite de convite vivem fora do Provider — não há
 * `me` ainda. Sem isto, a tela de cadastro abriria em inglês para o
 * brasileiro, e quem não entende a tela de cadastro não vira cliente. */
const LocaleContext = createContext<Contexto>({ idioma: idiomaInicial(), moeda: "USD" });

export function LocaleProvider({
  idioma,
  moeda,
  children,
}: {
  idioma: Idioma;
  moeda: string;
  children: ReactNode;
}) {
  return createElement(LocaleContext.Provider, { value: { idioma, moeda } }, children);
}

export function useLocale() {
  return useContext(LocaleContext);
}

/** O idioma do painel para uma sessão já autenticada.
 *
 * `User.locale` → `Garage.locale` → o que valia antes do login. A mesma
 * ordem de apps/common/locale.py: a recepcionista que entrou hoje não
 * deveria configurar idioma nenhum, e o dono brasileiro da oficina em
 * Miami quer o painel dele em português.
 *
 * Vive aqui, e não no Layout, porque **duas telas ficam fora do Layout
 * de propósito**: o assistente de configuração e a tela de assinatura
 * (esta última porque a conta suspensa não consegue abrir o painel, e é
 * justamente quem mais precisa pagar). Enquanto a regra morava lá, essas
 * duas caíam no default do contexto — moeda USD e idioma do navegador — e
 * era assim que a oficina brasileira via "$ 180,00" no onboarding. */
export function idiomaDoPainel(me?: { locale?: string; garage?: { locale?: string } } | null): Idioma {
  return normalizar(me?.locale) ?? normalizar(me?.garage?.locale) ?? idiomaInicial();
}

/** `const t = useT(); t("common.save")`
 *
 * Devolve a função, e não o texto, para a chamada ficar curta no JSX —
 * que é onde ela aparece centenas de vezes. */
export function useT() {
  const { idioma } = useLocale();
  return (chave: Chave, vars?: Vars) => traduzir(idioma, chave, vars);
}

/** Formatadores já amarrados ao idioma e à moeda desta oficina.
 *
 * Existe para o locale não ter de viajar de mão em mão por cada
 * `money()` e `hhmm()` do painel — são dezenas, e a que for esquecida
 * mostra o número no formato do outro país sem avisar ninguém. */
export function useFormat() {
  const { idioma, moeda } = useLocale();
  return {
    money: (valor: string | number) => fmt.money(valor, moeda, idioma),
    hhmm: (iso: string) => fmt.hhmm(iso, idioma),
    dayLabel: (iso: string) => fmt.dayLabel(iso, idioma),
    fullDate: (iso: string) => fmt.fullDate(iso, idioma),
    duration: (minutos: number) => fmt.duration(minutos, idioma),
    /** O telefone na máscara do país da OFICINA — o cliente dela está
     *  lá. (305) 555-0199 com máscara brasileira vira (30) 5555-0199. */
    phone: (valor: string, pais: string) => fmt.phone(valor, pais),
    weekday: (indice: number, estilo: "long" | "short" = "long") =>
      fmt.weekdayName(indice, idioma, estilo),
    /** "setembro de 2026" · "September 2026" — o título da visão de mês. */
    monthLabel: (d: Date) => fmt.monthLabel(d, idioma),
    /** O intervalo da semana escrito como o idioma escreve. */
    rangeLabel: (de: Date, ate: Date) => fmt.rangeLabel(de, ate, idioma),
    /** Os sete cabeçalhos da grade, do domingo ao sábado. */
    weekdayHeaders: (estilo: "short" | "narrow" = "short") =>
      fmt.weekdayHeaders(idioma, estilo),
    /** O número do dia na numeração do idioma — nem todo locale escreve
     *  os dias em algarismos ocidentais. */
    dayNumber: (d: Date) => d.toLocaleDateString(idioma, { day: "numeric" }),
    clock: (hhmm: string) => fmt.clockLabel(hhmm, idioma),
    odometer: (km: number | null, unidade: string) => fmt.odometer(km, unidade, idioma),
    /** Número cru para dentro de um `<input>`: sem símbolo, no separador
     *  decimal de quem digita. `money()` não serve aqui — "R$ 89,00" volta
     *  para o campo e deixa de ser editável. */
    decimalInput: (valor: string | number) => fmt.decimalInput(valor, idioma),
    locale: idioma,
    currency: moeda,
  };
}
